class TfpNewsletterCountry extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.placementController?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    // The custom element can connect before its parser-created children exist.
    queueMicrotask(() => {
    if (!this.isConnected || signal.aborted) return;
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;
    this.details.addEventListener('toggle', () => this.syncPlacement(), { signal });
    this.addEventListener('change', () => this.syncSelection(), { signal });
    this.addEventListener('focusout', (event) => {
      // A transient null/body focus during pointer activation is not a confirmed exit.
      const next = event.relatedTarget;
      if (this.details.open && next instanceof Node && !this.contains(next)) this.close(false);
    }, { signal });
    this.addEventListener('click', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio' && event.detail > 0) {
        setTimeout(() => { if (this.isConnected && !signal.aborted) this.close(true); }, 0);
      }
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (!this.details.open) return;
      if (event.key === 'Escape' || (event.key === 'Enter' && event.target instanceof HTMLInputElement)) {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      }
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.details.open && event.target instanceof Node && !this.contains(event.target)) this.close(false);
    }, { signal });
    this.closest('form')?.addEventListener('reset', () => {
      queueMicrotask(() => { if (this.isConnected) this.syncSelection(); });
    }, { signal });
    this.syncSelection();
    if (this.details.open) this.syncPlacement();
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.placementController?.abort();
  }

  syncPlacement() {
    this.placementController?.abort();
    if (!this.details.open) {
      this.removeAttribute('data-placement');
      this.style.removeProperty('--country-options-available');
      return;
    }
    this.placementController = new AbortController();
    const { signal } = this.placementController;
    const update = () => this.positionOptions();
    // Capture ancestor scrolling, including the modal scrollport, only while open.
    document.addEventListener('scroll', update, { capture: true, passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', update, { passive: true, signal });
    window.visualViewport?.addEventListener('scroll', update, { passive: true, signal });
    update();
  }

  positionOptions() {
    if (!this.details.open) return;
    const viewport = window.visualViewport;
    let top = viewport?.offsetTop ?? 0;
    let bottom = top + (viewport?.height ?? window.innerHeight);
    // Intersect every clipping ancestor; do not escape dialog/focus containment.
    for (let parent = this.parentElement; parent; parent = parent.parentElement) {
      if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(parent).overflowY)) continue;
      const bounds = parent.getBoundingClientRect();
      top = Math.max(top, bounds.top + parent.clientTop);
      bottom = Math.min(bottom, bounds.top + parent.clientTop + parent.clientHeight);
    }
    const anchor = this.getBoundingClientRect();
    const above = Math.max(0, anchor.top - top - 8);
    const below = Math.max(0, bottom - anchor.bottom - 8);
    const desired = Math.min(240, (viewport?.height ?? window.innerHeight) * 0.4);
    const upward = below < desired && above > below;
    this.dataset.placement = upward ? 'top' : 'bottom';
    this.style.setProperty('--country-options-available', `${Math.floor(Math.min(desired, upward ? above : below))}px`);
  }

  close(returnFocus) {
    this.details.open = false;
    this.syncPlacement();
    if (returnFocus) this.summary.focus({ preventScroll: true });
  }

  syncSelection() {
    const selected = this.querySelector('input[type="radio"]:checked');
    if (!selected) return;
    const code = this.summary.querySelector('[data-country-code]');
    if (code) code.textContent = selected.dataset.countryCode;
    this.summary.setAttribute('aria-label', `${this.dataset.countryLabel}: ${selected.dataset.countryName}`);
    let flag = this.summary.querySelector('img');
    if (!selected.dataset.countryFlag) { flag?.remove(); return; }
    if (!flag) {
      flag = document.createElement('img');
      flag.className = 'email-signup__country-flag';
      flag.alt = '';
      flag.width = 24;
      flag.height = 18;
      this.summary.prepend(flag);
    }
    if (flag.getAttribute('src') !== selected.dataset.countryFlag) {
      flag.removeAttribute('srcset');
      flag.src = selected.dataset.countryFlag;
    }
  }
}

if (!customElements.get('tfp-newsletter-country')) customElements.define('tfp-newsletter-country', TfpNewsletterCountry);
